/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 automations list (design reference "Automations · List"): each
 * automation with what starts it, its status, runs and success over the
 * last 30 days, and who last changed it. Clicking a row opens it on the
 * canvas. The actions are the list's existing ones (AutomationBuilder).
 *
 * Run figures come from /automations/{id}/stats, fetched for the rows on
 * the current page only.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MoreHorizontal, Search, Zap } from "lucide-react";
import { useAuth } from "auth/Auth";
import SegmentedControl from "components/v2/SegmentedControl";
import { ToneChip } from "components/v2/settings/SettingsBlocks";
import { v2 as t } from "styles/tokens/v2";
import { relativeTime } from "layouts/crm/account360/format";
import { describeTrigger } from "./canvasModel";

const PAGE = 10;

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

export default function AutomationsListV2({ automations, onOpen, onViewLogs, onToggleStatus, onRequestDelete }) {
  const { axiosInstance } = useAuth();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState({});
  const [menu, setMenu] = useState(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return automations
      .filter((a) => (status === "all" ? true : status === "active" ? a.is_active : !a.is_active))
      .filter((a) => !q || `${a.name} ${a.trigger_object} ${a.updated_by_name || ""}`.toLowerCase().includes(q))
      .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }, [automations, query, status]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE));
  const current = Math.min(page, pageCount - 1);
  const visible = rows.slice(current * PAGE, current * PAGE + PAGE);
  const visibleIds = visible.map((a) => a.id).join(",");

  useEffect(() => {
    let cancelled = false;
    visible
      .filter((a) => !(a.id in stats))
      .forEach((a) => {
        axiosInstance
          .get(`/automations/${a.id}/stats`, { params: { days: 30 } })
          .then(({ data }) => !cancelled && setStats((s) => ({ ...s, [a.id]: data })))
          .catch(() => !cancelled && setStats((s) => ({ ...s, [a.id]: null })));
      });
    return () => {
      cancelled = true;
    };
    // Fetch for the rows on this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIds]);

  const active = automations.filter((a) => a.is_active).length;
  const headCell = { fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: t.text3, px: 2, py: 1.25, textAlign: "left" };
  const cell = { px: 2, py: 1.5, borderTop: `1px solid ${t.border}`, fontSize: 14, color: t.text, verticalAlign: "middle" };

  return (
    <Box sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, px: 2, py: 1.5 }}>
        <SegmentedControl
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(0);
          }}
          options={[
            { value: "all", label: `All ${automations.length}` },
            { value: "active", label: `Active ${active}` },
            { value: "paused", label: `Paused ${automations.length - active}` },
          ]}
          label="Show automations"
        />
        <TextField
          size="small"
          placeholder="Search this list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          inputProps={{ "aria-label": "Search automations" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} color={t.text3} /></InputAdornment> }}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />
        <Typography sx={{ ml: "auto", fontSize: 14, color: t.text2 }}>
          {rows.length} automation{rows.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr>
              {["Automation", "Status", "Runs (30 days)", "Success", "Owner", "Last edited", ""].map((h) => (
                <Box component="th" key={h || "menu"} scope="col" sx={headCell}>
                  {h}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((a) => {
              const s = stats[a.id];
              const failed = s?.failed_executions || 0;
              return (
                <Box component="tr" key={a.id} sx={{ "&:hover": { bgcolor: t.hover } }}>
                  <Box component="td" sx={cell}>
                    <ButtonBase onClick={() => onOpen(a)} sx={{ display: "flex", alignItems: "center", gap: 1.25, fontFamily: "inherit", textAlign: "left" }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: "#E6EFFB", color: "#1453A0", flexShrink: 0 }}>
                        <Zap size={16} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: t.link }}>{a.name}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.text2 }}>{describeTrigger(a.trigger_object, a.trigger_event).replace(/^When/, `${String(a.trigger_object || "").replace(/_/g, " ").replace(/\b\w/, (c) => c.toUpperCase())} · when`)}</Typography>
                      </Box>
                    </ButtonBase>
                  </Box>
                  <Box component="td" sx={cell}>
                    {failed > 0 ? (
                      <ToneChip tone="danger">{`${failed} failure${failed === 1 ? "" : "s"}`}</ToneChip>
                    ) : (
                      <ToneChip tone={a.is_active ? "success" : "warning"}>{a.is_active ? "Active" : "Paused"}</ToneChip>
                    )}
                  </Box>
                  <Box component="td" sx={{ ...cell, fontVariantNumeric: "tabular-nums" }}>{s ? (s.total_executions || 0).toLocaleString() : "—"}</Box>
                  <Box component="td" sx={{ ...cell, fontVariantNumeric: "tabular-nums" }}>{s && s.total_executions ? `${s.success_rate}%` : "—"}</Box>
                  <Box component="td" sx={cell}>
                    {a.updated_by_name || a.created_by_name ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, bgcolor: "#EDF0F5", color: t.text2 }}>
                          {initials(a.updated_by_name || a.created_by_name)}
                        </Box>
                        {a.updated_by_name || a.created_by_name}
                      </Box>
                    ) : (
                      "—"
                    )}
                  </Box>
                  <Box component="td" sx={{ ...cell, color: t.text2 }}>{relativeTime(a.updated_at) || "—"}</Box>
                  <Box component="td" sx={{ ...cell, width: 48 }}>
                    <IconButton size="small" aria-label={`More actions for ${a.name}`} onClick={(e) => setMenu({ anchor: e.currentTarget, a })}>
                      <MoreHorizontal size={16} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>
      {!visible.length && (
        <Typography sx={{ py: 5, textAlign: "center", fontSize: 14, color: t.text2 }}>No automations match.</Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, borderTop: `1px solid ${t.border}` }}>
        <Typography sx={{ fontSize: 13.5, color: t.text2 }}>
          {rows.length ? `Showing ${current * PAGE + 1}–${Math.min(rows.length, current * PAGE + PAGE)} of ${rows.length}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <ButtonBase disabled={current === 0} onClick={() => setPage(current - 1)} sx={{ px: 1.5, height: 32, borderRadius: "8px", border: `1px solid ${t.border2}`, fontFamily: "inherit", fontSize: 13.5, "&.Mui-disabled": { opacity: 0.5 } }}>
            Previous
          </ButtonBase>
          <ButtonBase disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)} sx={{ px: 1.5, height: 32, borderRadius: "8px", border: `1px solid ${t.border2}`, fontFamily: "inherit", fontSize: 13.5, "&.Mui-disabled": { opacity: 0.5 } }}>
            Next
          </ButtonBase>
        </Box>
      </Box>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { onOpen(menu.a); setMenu(null); }}>Open</MenuItem>
        <MenuItem onClick={() => { onViewLogs(menu.a); setMenu(null); }}>Run history</MenuItem>
        <MenuItem onClick={() => { onToggleStatus(menu.a.id); setMenu(null); }}>{menu?.a?.is_active ? "Pause" : "Activate"}</MenuItem>
        <MenuItem onClick={() => { onRequestDelete(menu.a); setMenu(null); }} sx={{ color: "#B42318" }}>Delete</MenuItem>
      </Menu>
    </Box>
  );
}

AutomationsListV2.propTypes = {
  automations: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOpen: PropTypes.func.isRequired,
  onViewLogs: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func.isRequired,
};
